import React, { useEffect, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;

const DEFAULTS = {
  heading: "Something awesome is coming.",
  message:
    "DietBanao is almost here — a personalised wellness experience built around your body, your routine and your life. Leave your details and we'll tell you the moment we go live.",
  cta_label: "Notify Me",
  thank_you_title: "You're on the list!",
  thank_you_message: "Thank you! We'll be in touch the moment we launch.",
  form_enabled: true,
};

export default function ComingSoonPage() {
  const [cfg, setCfg] = useState(DEFAULTS);
  const [form, setForm] = useState({ name: "", email: "", mobile: "", company: "" });
  const [status, setStatus] = useState("idle"); // idle | submitting | done
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    fetch(`${API}/api/coming-soon`)
      .then((r) => r.json())
      .then((d) => {
        if (active && d?.config) setCfg((prev) => ({ ...prev, ...d.config }));
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim()) return setError("Please enter your name.");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) return setError("Please enter a valid email.");
    if (form.mobile.replace(/\D/g, "").length < 8) return setError("Please enter a valid mobile number.");
    setStatus("submitting");
    try {
      const res = await fetch(`${API}/api/coming-soon/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) {
        setStatus("idle");
        return setError(data?.detail || "Something went wrong. Please try again.");
      }
      setCfg((prev) => ({
        ...prev,
        thank_you_title: data.title || prev.thank_you_title,
        thank_you_message: data.message || prev.thank_you_message,
      }));
      setStatus("done");
    } catch (_e) {
      setStatus("idle");
      setError("Network error. Please try again.");
    }
  };

  return (
    <div data-testid="coming-soon-page" style={styles.page}>
      <div style={styles.glow} aria-hidden="true" />
      <div style={styles.inner}>
        <img
          src="/images/dietbanao-logo-256.webp"
          alt="DietBanao"
          width={96}
          height={96}
          style={styles.logo}
          data-testid="coming-soon-logo"
        />
        <div style={styles.kicker}>DietBanao</div>
        <h1 style={styles.heading} data-testid="coming-soon-heading">
          {cfg.heading}
        </h1>

        {status === "done" ? (
          <div style={styles.thankyou} data-testid="coming-soon-thankyou">
            <div style={styles.tyBadge}>✓</div>
            <h2 style={styles.tyTitle}>{cfg.thank_you_title}</h2>
            <p style={styles.message}>{cfg.thank_you_message}</p>
          </div>
        ) : (
          <>
            <p style={styles.message} data-testid="coming-soon-message">
              {cfg.message}
            </p>

            {cfg.form_enabled && (
              <form onSubmit={submit} style={styles.form} data-testid="coming-soon-form" noValidate>
                {/* honeypot */}
                <input
                  type="text"
                  name="company"
                  value={form.company}
                  onChange={update("company")}
                  tabIndex={-1}
                  autoComplete="off"
                  style={styles.honeypot}
                  aria-hidden="true"
                />
                <input
                  type="text"
                  placeholder="Your name"
                  value={form.name}
                  onChange={update("name")}
                  style={styles.input}
                  data-testid="coming-soon-name"
                  autoComplete="name"
                />
                <input
                  type="email"
                  placeholder="Email address"
                  value={form.email}
                  onChange={update("email")}
                  style={styles.input}
                  data-testid="coming-soon-email"
                  autoComplete="email"
                />
                <input
                  type="tel"
                  inputMode="numeric"
                  placeholder="Mobile number"
                  value={form.mobile}
                  onChange={update("mobile")}
                  style={styles.input}
                  data-testid="coming-soon-mobile"
                  autoComplete="tel"
                />
                {error && (
                  <div style={styles.error} data-testid="coming-soon-error">
                    {error}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  style={{ ...styles.button, opacity: status === "submitting" ? 0.7 : 1 }}
                  data-testid="coming-soon-submit"
                >
                  {status === "submitting" ? "Submitting…" : cfg.cta_label || "Notify Me"}
                </button>
              </form>
            )}
          </>
        )}

        <div style={styles.footer}>Coming Soon · {new Date().getFullYear()} DietBanao</div>
      </div>
    </div>
  );
}

const TEAL = "#0F5D4E";
const SAFFRON = "#F4A024";
const CREAM = "#FFF7E9";

const styles = {
  page: {
    minHeight: "100vh",
    background: CREAM,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    overflow: "hidden",
    padding: "24px",
    fontFamily: "'Montserrat', system-ui, sans-serif",
  },
  glow: {
    position: "absolute",
    top: "-20%",
    left: "50%",
    transform: "translateX(-50%)",
    width: "120vw",
    height: "70vh",
    background: `radial-gradient(ellipse at center, rgba(244,160,36,0.18) 0%, rgba(244,160,36,0) 60%)`,
    pointerEvents: "none",
  },
  inner: {
    position: "relative",
    zIndex: 1,
    width: "100%",
    maxWidth: "440px",
    textAlign: "center",
  },
  logo: { display: "block", margin: "0 auto 16px", borderRadius: "16px" },
  kicker: {
    fontFamily: "'Poppins', sans-serif",
    letterSpacing: "0.28em",
    textTransform: "uppercase",
    fontSize: "12px",
    fontWeight: 600,
    color: SAFFRON,
    marginBottom: "16px",
  },
  heading: {
    fontFamily: "'Poppins', sans-serif",
    fontWeight: 700,
    color: TEAL,
    fontSize: "clamp(28px, 7vw, 40px)",
    lineHeight: 1.15,
    margin: "0 0 16px",
  },
  message: {
    color: "#3d4a43",
    fontSize: "15px",
    lineHeight: 1.6,
    margin: "0 auto 28px",
    maxWidth: "400px",
  },
  form: { display: "flex", flexDirection: "column", gap: "12px", textAlign: "left" },
  honeypot: { position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0 },
  input: {
    width: "100%",
    padding: "14px 16px",
    borderRadius: "12px",
    border: "1.5px solid rgba(15,93,78,0.2)",
    background: "#fff",
    fontSize: "15px",
    color: "#1f2a25",
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "'Montserrat', sans-serif",
  },
  button: {
    width: "100%",
    padding: "15px 16px",
    borderRadius: "999px",
    border: "none",
    background: TEAL,
    color: "#fff",
    fontSize: "15px",
    fontWeight: 600,
    fontFamily: "'Poppins', sans-serif",
    cursor: "pointer",
    marginTop: "4px",
    transition: "background-color 0.2s ease, transform 0.1s ease",
  },
  error: { color: "#b42318", fontSize: "13px", padding: "2px 2px" },
  thankyou: { padding: "8px 0 16px" },
  tyBadge: {
    width: "56px",
    height: "56px",
    borderRadius: "50%",
    background: SAFFRON,
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "28px",
    margin: "0 auto 16px",
  },
  tyTitle: {
    fontFamily: "'Poppins', sans-serif",
    fontWeight: 700,
    color: TEAL,
    fontSize: "22px",
    margin: "0 0 10px",
  },
  footer: {
    marginTop: "36px",
    fontSize: "12px",
    letterSpacing: "0.1em",
    color: "rgba(15,93,78,0.5)",
    textTransform: "uppercase",
  },
};
